import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Briefcase, CheckCircle2, ArrowRight, Sparkles, DollarSign,
  BarChart3, Users, ShieldCheck, Zap, Receipt, Clock, Layers,
  ChevronRight, ArrowUpRight, Server, Lock, Filter, RefreshCw
} from "lucide-react";
import { Reveal, SectionLabel, Stagger, StaggerItem, Highlight } from "@/components/motion";
import CTABanner from "@/components/CTABanner";

const MODULES = [
  {
    icon: Filter,
    title: "Visual Pipeline & Stage Automation",
    desc: "Drag-and-drop kanban deal boards with automated stage triggers, follow-up alerts, and predictive deal velocity scoring.",
    points: ["Customizable pipeline stages", "Automated email sequences", "Deal rotting & SLA indicators", "Probability-weighted forecasts"],
  },
  {
    icon: Users,
    title: "Client & Account 360° Records",
    desc: "Consolidate every email thread, contract file, meeting note, past invoice, and active project milestone into one real-time account hub.",
    points: ["Two-way email & calendar sync", "Activity timelines & audit logs", "Custom custom fields & tags", "Role-based account ownership"],
  },
  {
    icon: Receipt,
    title: "Invoicing, Contracts & Payments",
    desc: "Generate professional recurring invoices, sign e-contracts, and collect global payments via Stripe and bank transfer automatically.",
    points: ["Automated recurring billing", "Multi-currency support", "Built-in e-signature approval", "QuickBooks & Xero two-way sync"],
  },
  {
    icon: BarChart3,
    title: "Revenue & Team Capacity Intelligence",
    desc: "Turn messy pipeline numbers into actionable monthly recurring revenue (MRR) projections and team billable hour capacity planning.",
    points: ["Executive MRR dashboards", "Billable hour utilization", "Win/Loss analysis reports", "Custom CSV/API exports"],
  },
];

const AUDIENCES = [
  {
    title: "Digital Agencies & Studios",
    desc: "Manage creative retainers, project scopes, client communication, and milestone billing without juggling 5 different subscriptions.",
    metrics: "12 hours saved weekly per project manager",
  },
  {
    title: "B2B Tech Consultancies",
    desc: "Track complex enterprise deal cycles, multi-stakeholder decisions, statement of works (SOWs), and engineer allocations.",
    metrics: "28% shorter sales cycle duration",
  },
  {
    title: "Professional Services & Advisors",
    desc: "Client onboarding checklists, secure document exchanges, automated retainer invoicing, and calendar appointments.",
    metrics: "96% client portal engagement rate",
  },
];

const TECH_SPECS = [
  { icon: Server, label: "Integrations", val: "Stripe · QuickBooks · Slack · Gmail / Outlook" },
  { icon: Lock, label: "Data Protection", val: "End-to-End Encryption · SOC 2 Ready" },
  { icon: Layers, label: "Extensibility", val: "Full REST & Webhooks Architecture" },
  { icon: Clock, label: "Platform SLA", val: "99.99% Guaranteed High Availability" },
];

export default function ProductCRM() {
  return (
    <div data-testid="product-crm-page">
      {/* HERO */}
      <section className="relative pt-36 pb-20 px-5 sm:px-8 overflow-hidden bg-white" data-testid="crm-hero-section">
        <div className="absolute inset-0 bg-gradient-to-b from-[#CAF2F9]/30 to-white pointer-events-none" />
        <div className="absolute -top-24 left-1/4 w-[500px] h-[500px] rounded-full bg-[#2563EB]/15 blur-3xl pointer-events-none" />
        <div className="absolute inset-0 dot-grid opacity-40 pointer-events-none" />

        <div className="relative max-w-7xl mx-auto">
          <div className="max-w-4xl mx-auto text-center">
            <Reveal>
              <div className="flex items-center justify-center gap-2 mb-4">
                <span className="font-mono-brand inline-flex items-center gap-2 bg-[#CAF2F9]/60 border border-[#91E0F1]/50 text-[#2563EB] text-[11px] font-semibold tracking-[0.16em] uppercase px-3.5 py-1.5 rounded-full">
                  <Briefcase size={14} className="text-[#2563EB]" /> Operations & Pipeline OS
                </span>
              </div>
              <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#01193D] leading-[1.1]">
                The Aurge Labs - <span className="text-gradient">CRM</span>
              </h1>
              <p className="font-display font-medium text-xl sm:text-2xl text-[#2563EB] mt-4">
                Every deal, contract, and client interaction in one unified operating system.
              </p>
              <p className="text-[#475569] text-base lg:text-lg leading-relaxed mt-4 max-w-2xl mx-auto">
                Built for high-velocity teams who demand total clarity across sales pipelines, client relationships, project delivery, and billing automation.
              </p>
              <div className="flex flex-wrap items-center justify-center gap-4 mt-8">
                <Link
                  to="/contact"
                  data-testid="crm-hero-demo-cta"
                  className="btn-gradient text-white text-sm font-semibold px-8 py-3.5 rounded-full inline-flex items-center gap-2"
                >
                  Schedule Live CRM Demo <ArrowRight size={16} />
                </Link>
                <a
                  href="#modules"
                  data-testid="crm-hero-explore-cta"
                  className="btn-outline-navy text-sm font-semibold px-7 py-3.5 rounded-full inline-flex items-center gap-1.5"
                >
                  Explore Capabilities <ChevronRight size={15} />
                </a>
              </div>
            </Reveal>
          </div>

          {/* Interactive CRM Pipeline Live Preview */}
          <Reveal delay={0.25} className="mt-14 max-w-5xl mx-auto">
            <div className="rounded-3xl bg-white soft-shadow border border-slate-200/90 overflow-hidden shadow-2xl">
              {/* Top Bar */}
              <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-100 bg-[#F8FAFC]">
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-rose-400/80" />
                  <span className="w-3 h-3 rounded-full bg-amber-400/80" />
                  <span className="w-3 h-3 rounded-full bg-emerald-400/80" />
                  <div className="ml-3 h-6 px-3 rounded-md bg-white border border-slate-200 flex items-center">
                    <span className="font-mono text-[10px] text-slate-500">app.crm.theaurgelabs.com/pipeline/deals</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-[#2563EB] animate-pulse" />
                  <span className="font-mono text-[10px] text-[#2563EB] font-semibold">Q3 Pipeline: $485,000</span>
                </div>
              </div>

              {/* Kanban Deals Board Canvas */}
              <div className="p-6 sm:p-8 bg-slate-50/50">
                {/* Metric Summary Ribbon */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                  {[
                    { label: "Active Deals in Pipeline", val: "$485,000", change: "24 opportunities", color: "#2563EB" },
                    { label: "Avg. Deal Close Time", val: "18 Days", change: "-6 days vs benchmark", color: "#09B2ED" },
                    { label: "Monthly Recurring (MRR)", val: "$62,400", change: "+14.8% growth", color: "#2563EB" },
                    { label: "Win Rate Percentage", val: "34.2%", change: "Top quartile", color: "#09B2ED" },
                  ].map((stat, i) => (
                    <div key={i} className="bg-white rounded-2xl p-4 border border-slate-100 soft-shadow">
                      <div className="font-mono text-[10px] uppercase text-slate-400 font-semibold">{stat.label}</div>
                      <div className="font-display font-bold text-2xl text-[#01193D] mt-1">{stat.val}</div>
                      <div className="font-mono text-[10px] text-emerald-600 font-medium mt-1">{stat.change}</div>
                    </div>
                  ))}
                </div>

                {/* Pipeline Stages Grid */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Stage 1: Proposal Sent */}
                  <div className="bg-white rounded-2xl border border-slate-100 p-4 soft-shadow">
                    <div className="flex items-center justify-between pb-3 border-b border-slate-100">
                      <span className="font-mono text-xs font-bold text-[#01193D]">Proposal Sent (3)</span>
                      <span className="font-mono text-xs text-slate-400">$140k</span>
                    </div>
                    <div className="space-y-3 mt-3">
                      {[
                        { client: "Nexus Cloud Corp", value: "$65,000", badge: "High Intent", days: "2d ago" },
                        { client: "Horizon Health", value: "$45,000", badge: "Reviewing SOW", days: "4d ago" },
                        { client: "Vanguard Retail", value: "$30,000", badge: "Legal Check", days: "1d ago" },
                      ].map((deal, i) => (
                        <div key={i} className="p-3 rounded-xl bg-slate-50 border border-slate-100 hover:border-[#2563EB]/40 transition-colors">
                          <div className="flex justify-between items-start">
                            <span className="text-xs font-bold text-[#01193D]">{deal.client}</span>
                            <span className="font-mono text-xs font-bold text-[#2563EB]">{deal.value}</span>
                          </div>
                          <div className="flex justify-between items-center mt-2.5">
                            <span className="text-[9px] bg-[#CAF2F9] text-[#01193D] px-2 py-0.5 rounded-full font-medium">{deal.badge}</span>
                            <span className="font-mono text-[9px] text-slate-400">{deal.days}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Stage 2: In Negotiation */}
                  <div className="bg-white rounded-2xl border border-slate-100 p-4 soft-shadow">
                    <div className="flex items-center justify-between pb-3 border-b border-slate-100">
                      <span className="font-mono text-xs font-bold text-[#01193D]">Negotiation (2)</span>
                      <span className="font-mono text-xs text-slate-400">$195k</span>
                    </div>
                    <div className="space-y-3 mt-3">
                      {[
                        { client: "Meridian Logistics", value: "$120,000", badge: "Contract Sent", days: "Today" },
                        { client: "Apex Fintech", value: "$75,000", badge: "Redlines Clear", days: "1d ago" },
                      ].map((deal, i) => (
                        <div key={i} className="p-3 rounded-xl bg-slate-50 border border-slate-100 hover:border-[#2563EB]/40 transition-colors">
                          <div className="flex justify-between items-start">
                            <span className="text-xs font-bold text-[#01193D]">{deal.client}</span>
                            <span className="font-mono text-xs font-bold text-[#2563EB]">{deal.value}</span>
                          </div>
                          <div className="flex justify-between items-center mt-2.5">
                            <span className="text-[9px] bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full font-medium">{deal.badge}</span>
                            <span className="font-mono text-[9px] text-slate-400">{deal.days}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Stage 3: Closed Won */}
                  <div className="bg-white rounded-2xl border border-slate-100 p-4 soft-shadow">
                    <div className="flex items-center justify-between pb-3 border-b border-slate-100">
                      <span className="font-mono text-xs font-bold text-emerald-700">Closed Won (3)</span>
                      <span className="font-mono text-xs text-emerald-600 font-bold">$150k</span>
                    </div>
                    <div className="space-y-3 mt-3">
                      {[
                        { client: "Oakridge Institute", value: "$60,000", badge: "Deposit Paid", days: "Today" },
                        { client: "Brightside Media", value: "$55,000", badge: "Onboarding", days: "Yesterday" },
                        { client: "Solstice Bio", value: "$35,000", badge: "Active Project", days: "3d ago" },
                      ].map((deal, i) => (
                        <div key={i} className="p-3 rounded-xl bg-emerald-50/50 border border-emerald-100">
                          <div className="flex justify-between items-start">
                            <span className="text-xs font-bold text-[#01193D]">{deal.client}</span>
                            <span className="font-mono text-xs font-bold text-emerald-700">{deal.value}</span>
                          </div>
                          <div className="flex justify-between items-center mt-2.5">
                            <span className="text-[9px] bg-emerald-200 text-emerald-900 px-2 py-0.5 rounded-full font-medium">{deal.badge}</span>
                            <span className="font-mono text-[9px] text-slate-400">{deal.days}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CORE MODULES */}
      <section id="modules" className="px-5 sm:px-8 py-20 lg:py-28 bg-[#F8FAFC] scroll-mt-24" data-testid="crm-modules-section">
        <div className="max-w-7xl mx-auto">
          <Reveal className="max-w-3xl">
            <SectionLabel>Core Capabilities</SectionLabel>
            <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-[42px] tracking-tight text-[#01193D] leading-tight">
              Engineered to drive revenue and eliminate operational drag
            </h2>
            <p className="text-[#475569] text-base lg:text-lg mt-4 leading-relaxed">
              Every tool required to convert leads, manage client accounts, automate invoices, and measure team performance.
            </p>
          </Reveal>

          <Stagger className="grid md:grid-cols-2 gap-6 mt-14">
            {MODULES.map((m) => (
              <StaggerItem key={m.title} className="card-lift bg-white rounded-3xl p-8 border border-slate-100 soft-shadow">
                <span className="w-12 h-12 rounded-2xl bg-[#CAF2F9]/60 border border-[#91E0F1]/40 flex items-center justify-center text-[#2563EB] mb-6">
                  <m.icon size={24} />
                </span>
                <h3 className="font-display font-bold text-xl text-[#01193D]">{m.title}</h3>
                <p className="text-[#475569] text-sm leading-relaxed mt-2.5">{m.desc}</p>
                <ul className="grid sm:grid-cols-2 gap-2.5 mt-6 pt-6 border-t border-slate-100">
                  {m.points.map((pt) => (
                    <li key={pt} className="flex items-center gap-2 text-xs font-medium text-[#01193D]">
                      <CheckCircle2 size={14} className="text-[#09B2ED] shrink-0" />
                      {pt}
                    </li>
                  ))}
                </ul>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* WHO IT'S BUILT FOR */}
      <section className="px-5 sm:px-8 py-20 lg:py-24 bg-white" data-testid="crm-audiences-section">
        <div className="max-w-7xl mx-auto">
          <Reveal className="max-w-3xl">
            <SectionLabel>Target Audiences</SectionLabel>
            <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight text-[#01193D]">
              Built for high-touch service organizations
            </h2>
          </Reveal>

          <div className="grid md:grid-cols-3 gap-6 mt-12">
            {AUDIENCES.map((aud, i) => (
              <Reveal key={aud.title} delay={i * 0.1} className="rounded-3xl border border-slate-100 p-8 bg-slate-50/50 flex flex-col justify-between">
                <div>
                  <span className="font-mono text-xs font-semibold uppercase tracking-wider text-[#2563EB]">Use Case 0{i + 1}</span>
                  <h3 className="font-display font-bold text-xl text-[#01193D] mt-2">{aud.title}</h3>
                  <p className="text-sm text-[#475569] leading-relaxed mt-3">{aud.desc}</p>
                </div>
                <div className="mt-6 pt-4 border-t border-slate-200/80 font-mono text-xs font-semibold text-emerald-600">
                  ★ {aud.metrics}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TECHNICAL SPECIFICATIONS */}
      <section className="px-5 sm:px-8 py-16 bg-[#01193D] text-white" data-testid="crm-specs-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {TECH_SPECS.map((spec) => (
              <div key={spec.label} className="p-6 rounded-2xl bg-white/5 border border-white/10">
                <spec.icon size={20} className="text-[#09B2ED] mb-3" />
                <div className="text-xs text-slate-400 font-mono">{spec.label}</div>
                <div className="text-sm font-semibold text-white mt-1">{spec.val}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BOTTOM CTA */}
      <CTABanner
        title="Scale your pipeline with The Aurge Labs - CRM"
        subtitle="Schedule a personalized demo to see how your team can consolidate clients, deals, and billing in one system."
        cta="Book a CRM Walkthrough"
      />
    </div>
  );
}
